import {
   Box,
   Collapse,
   FormControl,
   FormErrorMessage,
   FormLabel,
   Menu,
   MenuButton,
   MenuItemOption,
   MenuList,
   MenuOptionGroup,
   Portal,
   Text
} from '@chakra-ui/react'
import { Icon } from 'components/atoms/Icon'
import { FocusEventHandler, useMemo } from 'react'
import { FieldError } from 'react-hook-form'
import { optionsDefault } from '../SelectBuild/constants'
import { SelectBuildProps, SelectOptions } from '../SelectBuild/interfaces'

export interface MultiSelectBuildProps {
    id: string
    label?: string
    options?: SelectOptions[]
    optionsType?: SelectBuildProps['optionsType']
    placeholder?: string
    value?: string
    defaultValue?: string
    err?: FieldError
    required?: boolean
    isDisable?: boolean
    onChange?: (value: string) => void
    onBlur?: FocusEventHandler<HTMLButtonElement>
}

export const MultiSelectBuild: React.FC<MultiSelectBuildProps> = ({
    id,
    label,
    options,
    optionsType,
    placeholder = 'Clique para selecionar',
    value = '',
    err,
    required,
    isDisable,
    onChange = () => undefined,
    onBlur
}) => {
    const optionsCurrent = useMemo<SelectOptions[]>(
        () => (optionsType && optionsDefault[optionsType]) || options || [],
        [optionsType, options]
    )

    const selectedValues = useMemo(
        () => value.split(',').filter(Boolean),
        [value]
    )

    const selectedLabel = useMemo(
        () =>
            optionsCurrent
                .filter((option) =>
                    selectedValues.includes(String(option.value))
                )
                .map((option) => option.text)
                .join(', '),
        [optionsCurrent, selectedValues]
    )

    const handleChange = (nextValue: string | string[]) => {
        const values = Array.isArray(nextValue) ? nextValue : [nextValue]
        onChange(values.join(','))
    }

    return (
        <FormControl isInvalid={!!err} isRequired={required}>
            <FormLabel htmlFor={id}>{label}</FormLabel>

            <Menu
                closeOnSelect={false}
                matchWidth
                placement="bottom-start"
                strategy="fixed"
            >
                {({ isOpen }) => (
                    <>
                        <MenuButton
                            id={id}
                            data-testid={id}
                            aria-label={label || id}
                            aria-invalid={!!err || undefined}
                            type="button"
                            width="100%"
                            height="40px"
                            paddingX={4}
                            paddingRight="2.5rem"
                            position="relative"
                            borderWidth="1px"
                            borderStyle="solid"
                            borderColor={err ? 'red.500' : 'gray.500'}
                            borderRadius="md"
                            backgroundColor={isDisable ? 'gray.100' : 'white'}
                            fontWeight="normal"
                            textAlign="left"
                            disabled={isDisable}
                            onBlur={onBlur}
                            _hover={{
                                borderColor: err ? 'red.500' : 'gray.600'
                            }}
                            _focus={{
                                borderColor: err ? 'red.500' : 'blue.500',
                                boxShadow: err
                                    ? '0 0 0 1px var(--chakra-colors-red-500)'
                                    : '0 0 0 1px var(--chakra-colors-blue-500)'
                            }}
                            _disabled={{
                                cursor: 'not-allowed',
                                opacity: 0.4
                            }}
                        >
                            <Text
                                color="inherit"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                            >
                                {selectedLabel || placeholder}
                            </Text>

                            <Box
                                pointerEvents="none"
                                position="absolute"
                                top="50%"
                                right="8px"
                                transform="translateY(-50%)"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Icon
                                    name={
                                        isOpen
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    }
                                />
                            </Box>
                        </MenuButton>

                        <Portal>
                            <MenuList
                                maxHeight="280px"
                                overflowY="auto"
                                zIndex={1500}
                            >
                                <MenuOptionGroup
                                    type="checkbox"
                                    value={selectedValues}
                                    onChange={handleChange}
                                >
                                    {optionsCurrent
                                        .filter((option) => !option.hidden)
                                        .map(
                                            ({
                                                value: optionValue,
                                                text,
                                                disabled
                                            }) => (
                                                <MenuItemOption
                                                    data-testid={`option-${id}-${optionValue}`}
                                                    key={`option-${id}-${optionValue}`}
                                                    value={String(optionValue)}
                                                    isDisabled={disabled}
                                                >
                                                    {text}
                                                </MenuItemOption>
                                            )
                                        )}
                                </MenuOptionGroup>
                            </MenuList>
                        </Portal>
                    </>
                )}
            </Menu>

            <Collapse in={!!err} animateOpacity>
                <Box minH="15px">
                    <FormErrorMessage
                        position="relative"
                        marginTop={1}
                        fontSize="xs"
                    >
                        {err?.message}
                    </FormErrorMessage>
                </Box>
            </Collapse>
        </FormControl>
    )
}